import { create } from "zustand";
import type { ColumnMeta } from "../type";
import { Backend } from "../url";

export const TABLE_COLUMNS = [
  { id: "lnp_id", label: "LNP ID" },
  { id: "library_code", label: "Library Code" },
  { id: "il", label: "Ionizable Lipid (IL)" },
  { id: "hl", label: "Helper Lipid (HL)" },
  { id: "chl", label: "Cholesterol (CHL)" },
  { id: "peg", label: "PEG Lipid (PEG)" },
  { id: "il_molratio", label: "IL (Mol %)" },
  { id: "hl_molratio", label: "HL (Mol %)" },
  { id: "chl_molratio", label: "CHL (Mol %)" },
  { id: "peg_molratio", label: "PEG (Mol %)" },
  { id: "mass_ratio", label: "IL to Nucleic Acid Mass Ratio" },
  { id: "method", label: "Experimental Method" },
  { id: "value", label: "Experimental Value" },
  { id: "actions", label: "Copy to Clipboard" },
];

export const TABLE_OPTIONS = [
  { id: "has_disulfide", label: "Contains Disulfide Bond" },
  { id: "has_ester", label: "Contains Ester" },
  { id: "has_carbonate", label: "Contains Carbonate" },
  { id: "nitrogen_count", label: "Nitrogen Count" },
  { id: "molecular_weight", label: "Molecular Weight" },
  { id: "heavy_atoms", label: "Heavy Atom Count" },
  { id: "rings", label: "Ring Count" },
  { id: "aromatic_rings", label: "Aromatic Ring Count" },
  { id: "rotatable_bonds", label: "Rotatable Bond Count" },
  { id: "van_der_waals_volume", label: "Van der Waals Volume" },
  { id: "hydrogen_bond_donors", label: "Hydrogen Bond Donors" },
  { id: "hydrogen_bond_acceptors", label: "Hydrogen Bond Acceptors" },
  { id: "logp", label: "LogP" },
  { id: "molar_refractivity", label: "Molar Refractivity" },
  { id: "fraction_sp3_carbons", label: "Fraction sp3 Carbons" },
  { id: "sp3_carbons", label: "sp3 Carbons Count" },
  { id: "cargo_type", label: "Cargo Type" },
  { id: "cargo", label: "Cargo Name" },
  { id: "model", label: "Model Name" },
  { id: "model_target", label: "Model Delivery Target" },
  { id: "route_of_administration", label: "Route of Administration" },
  { id: "experiment_batching", label: "Experiment Batching" },
  { id: "created_by", label: "Created By" },
  { id: "created_at", label: "Created At" },
];

export const ALWAYS_VISIBLE_COLUMNS = ["lnp_id", "library_code"];

export const ALL_TABLE_COLUMNS = [
  ...TABLE_COLUMNS.filter((col) => col.id !== "actions"),
  ...TABLE_OPTIONS,
  ...TABLE_COLUMNS.filter((col) => col.id === "actions"),
];

export const DEFAULT_VISIBLE_COLUMNS = TABLE_COLUMNS.map((col) => col.id);

interface TableState {
  structureSearchParams: {
    type: string;
    smiles: string;
    threshold?: number | null;
  } | null;
  setStructureSearchParams: (
    params: {
      type: string;
      smiles: string;
      threshold?: number;
    } | null
  ) => void;
  columns: ColumnMeta[] | null;

  visibleColumns: string[];
  setVisibleColumns: (columns: string[]) => void;
  toggleColumnVisibility: (columnKey: string) => void;

  lastUpdated: number | null;
  isLoading: boolean;
  error: string | null;
  activeFilters: Record<string, any>;
  fetchColumns: () => Promise<void>;
  getEditableColumns: () => ColumnMeta[];
  setFilter: (key: string, value: any) => void;
  setFilters: (filters: Record<string, any>) => void;
  resetFilter: () => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  needsRefresh: () => boolean;
}

const REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000;

const useTableStore = create<TableState>()((set, get) => ({
  structureSearchParams: null,
  columns: null,

  visibleColumns: DEFAULT_VISIBLE_COLUMNS,

  lastUpdated: null,
  isLoading: false,
  error: null,
  activeFilters: {},

  setVisibleColumns: (columns) => {
    set({ visibleColumns: columns });
  },

  toggleColumnVisibility: (columnKey) => {
    set((state) => {
      const isVisible = state.visibleColumns.includes(columnKey);

      if (ALWAYS_VISIBLE_COLUMNS.includes(columnKey)) {
        return state;
      }

      if (isVisible) {
        return {
          visibleColumns: state.visibleColumns.filter((c) => c !== columnKey),
        };
      } else {
        const newVisible = [...state.visibleColumns, columnKey];
        const allColumnIds = ALL_TABLE_COLUMNS.map((col) => col.id);
        const sortedVisible = allColumnIds.filter((id) =>
          newVisible.includes(id)
        );
        return { visibleColumns: sortedVisible };
      }
    });
  },

  needsRefresh: () => {
    const { lastUpdated } = get();
    if (!lastUpdated) return true;
    return Date.now() - lastUpdated > REFRESH_INTERVAL;
  },

  fetchColumns: async () => {
    if (get().columns && !get().needsRefresh()) return;
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${Backend}/search/columns/`);
      if (!response.ok) {
        throw new Error(`Failed to fetch columns: ${response.statusText}`);
      }
      const data = (await response.json()) as ColumnMeta[];
      set({ columns: data, lastUpdated: Date.now(), isLoading: false });
    } catch (error) {
      console.error("Error fetching columns:", error);
      set({
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
        isLoading: false,
      });
    }
  },

  getEditableColumns: () => {
    const cols = get().columns;
    if (!cols) return [];
    return cols.filter((c) => c.editable);
  },

  setFilter: (key, value) => {
    set((state) => {
      if (value === null || value === undefined || value === "") {
        const newFilters = { ...state.activeFilters };
        delete newFilters[key];
        return { activeFilters: newFilters };
      }
      return {
        activeFilters: {
          ...state.activeFilters,
          [key]: value,
        },
      };
    });
  },

  setFilters: (filters) => {
    set({ activeFilters: { ...filters } });
  },

  resetFilter: () => {
    set({ activeFilters: {} });
  },

  setStructureSearchParams: (params) => {
    set({ structureSearchParams: params });
  },

  clearFilters: () => set({ activeFilters: {} }),

  hasActiveFilters: () => {
    return Object.keys(get().activeFilters).length > 0;
  },
}));

export default useTableStore;
